/**
 * src/logic/Settings.js
 *
 * Starter-preset default settings (D-04) for the Randovania reachability engine.
 *
 * Until the Phase 3 settings UI ships, the engine assumes Randovania's starter preset:
 *   - tricks disabled (level 0)
 *   - vanilla doors (DoorLocks off)
 *   - NerfPowerBombs on
 *   - vanilla teleporters
 *   - HighDanger off
 *   - energy_per_tank 100
 *   - immediate energy parts on
 *   - X not started released
 *   - damage_strictness 1.5
 *   - starting inventory: the preset's "must_start" pickups — Missile launcher (+15
 *     missiles), Pulse Radar, and Slide (Randovania starter_preset.rdvpreset:
 *     standard_pickup_configuration.pickups_state.{Missiles,Pulse Radar,Slide}
 *     num_included_in_starting_pickups = 1). The Missiles pickup has
 *     starting_condition "must_start" — you ALWAYS begin with the launcher + 15 ammo.
 *
 * Phase 3 will drive these values from UI/localStorage. Settings are a plain object.
 *
 * IMPORTANT: ZERO Node built-in imports. ZERO Vue/Vuex imports.
 */

// Base missile capacity granted by the Missile launcher in Metroid Dread.
export const STARTING_MISSILES = 15;

/**
 * Return the D-04 starter-preset default settings.
 *
 * @param {object} header - parsed header.json (provides starting_location, resource_database, used_trick_levels)
 * @returns {{
 *   trickLevel: number,
 *   trickLevels: { [trickKey: string]: number },
 *   requiredDna: number,
 *   misc: { SeparateBeams: number, SeparateMissiles: number, DoorLocks: number,
 *            HighDanger: number, Teleporters: number, NerfPowerBombs: number },
 *   energyPerTank: number,
 *   immediateParts: boolean,
 *   strictness: number,
 *   startingLocation: object,
 *   victory: string,
 *   startingItems: { MissileLauncher: number, MissileAmmo: number, Pulse: number, Slide: number }
 * }}
 */
export function defaultSettings(header) {
  // Build the per-trick level map from header.resource_database.tricks (all 26 keys → 0).
  // All tricks are disabled by default (matches the current flat trickLevel: 0 semantics,
  // now expressed per-trick so the engine's trickLevels branch is always used).
  const tricks =
    (header.resource_database && header.resource_database.tricks) || {};
  const trickLevels = {};
  for (const key of Object.keys(tricks)) {
    trickLevels[key] = 0;
  }

  return {
    trickLevel: 0, // harmless fallback; engine prefers trickLevels when present
    // Per-trick difficulty map (SET-06). Keys match resource_database.tricks exactly.
    // Defaults to 0 (disabled) for every trick. A single-slider patch from the UI
    // dispatches setSettings({ trickLevels: { IBJ: 3 } }) which is deep-merged by
    // SET_SETTINGS so sibling keys are never dropped.
    trickLevels,
    // Configurable DNA count for the victory gate (LOG-01). Default 3 = Randovania
    // Dread common default (starter preset). The 12 DNA slots still exist in the
    // items module; the UI shows slice(0, requiredDna) = 3 by default. Must be
    // clamped to [1,12] on write (SET_SETTINGS in logic.js handles clamping).
    requiredDna: 3,
    misc: {
      // Starter preset shuffles beams/missiles as SEPARATE items (not progressive),
      // so the logic must model in-game stacking: a higher beam opens a lower beam's
      // door (Wave→Wide/Plasma), and a higher missile opens a lower missile's door.
      // These flags gate the stacking branches of the Shoot * templates.
      SeparateBeams: 1,
      SeparateMissiles: 1,
      DoorLocks: 0,
      HighDanger: 0,
      Teleporters: 0,
      NerfPowerBombs: 1,
    },
    energyPerTank: 100,
    immediateParts: true,
    strictness: 1.5,
    startingLocation: header.starting_location,
    victory: "Ship",
    // Preset "must_start" starting inventory (Randovania short-names → quantity).
    // MissileAmmo is additive with collected missile tanks; the others are presence
    // flags. Power Beam is granted unconditionally by buildResourceState.
    startingItems: {
      MissileLauncher: 1,
      MissileAmmo: STARTING_MISSILES,
      Pulse: 1,
      Slide: 1,
    },
  };
}
